/**
 * TopNavAvatarMenu — Brief 51.1 (14 Jul 2026) TopNav cleanup.
 *
 * Right-hand avatar dropdown that collapses the director/admin surfaces
 * (App Settings, System Status, Directors Lounge, Reports, Run Patrol) and
 * Logout under a single trigger. The three former director-only chips
 * lived in the flat chip row and left non-directors staring at 8 chips
 * and directors at 11; this menu drops both to a common 6-chip row
 * plus one avatar circle.
 *
 * Behavioural contract locked with V's spec:
 *   * Trigger: circle with the user's initials (uppercased). Same
 *     resolution path the rest of the app uses — never a second lookup.
 *   * Open on click; close on Escape, click-outside, or item activation.
 *   * Arrow-key nav wraps top-bottom within the enabled items.
 *   * Focus returns to the trigger button on close.
 *   * Director-gated items only appear when ``isDirector`` is true;
 *     Run Patrol only when ``isAdmin`` is true; both flags come from the
 *     parent (TopNavBar) which already fetches ``/user-role`` once.
 *   * BM-H626 (26 Sep 2026): one "Directors Lounge" entry opens
 *     ``/directors``, whose tabs hold what three separate director
 *     entries used to open.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings,
  Crown,
  Radar,
  LogOut,
  ChevronDown,
  Loader2,
  BarChart3,
  Activity,
} from "lucide-react";
import { API } from "../config/api";
import { useAuth } from "../App";

import { errorMessage } from "../utils/errorMessage";
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

function resolveInitials(email) {
  const raw = (email || "").split("@")[0] || "?";
  const parts = raw.split(/[.\-_]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return raw.slice(0, 2).toUpperCase();
}

export default function TopNavAvatarMenu({
  currentUserEmail,
  isDirector,
  isAdmin,
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [patrolRunning, setPatrolRunning] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [focusIndex, setFocusIndex] = useState(0);

  const initials = resolveInitials(currentUserEmail);

  const items = [
    isDirector && {
      key: "app-settings",
      label: "App Settings",
      icon: Settings,
      onClick: () => navigate("/admin/tools"),
      testid: "avatar-menu-app-settings",
    },
    isDirector && {
      key: "system-status",
      label: "System Status",
      icon: Activity,
      onClick: () => navigate("/admin/system-status"),
      testid: "avatar-menu-system-status",
    },
    // BM-H626 (26 Sep 2026): one Directors Lounge replaces Directors
    // Analytics, Director Admin and Director Dashboard (now its tabs).
    isDirector && {
      key: "directors-lounge",
      label: "Directors Lounge",
      icon: Crown,
      onClick: () => navigate("/directors"),
      testid: "avatar-menu-directors-lounge",
    },
    isDirector && {
      key: "reports",
      label: "Reports",
      icon: BarChart3,
      onClick: () => navigate("/reports"),
      testid: "avatar-menu-reports",
    },
    isAdmin && {
      key: "run-patrol",
      label: patrolRunning ? "Running..." : "Run Patrol",
      icon: patrolRunning ? Loader2 : Radar,
      iconSpin: patrolRunning,
      disabled: patrolRunning,
      onClick: async () => {
        if (patrolRunning) return;
        setPatrolRunning(true);
        try {
          const headers = currentUserEmail
            ? { "X-Bonsai-User-Id": currentUserEmail }
            : {};
          const res = await axios.post(`${API}/mate/patrol/run`, {}, { headers });
          const created = res?.data?.alerts_created ?? res?.data?.created ?? 0;
          toast.success(`Patrol complete: ${created} new alerts`);
        } catch (err) {
          toast.error(errorMessage(err, "Patrol run failed"));
        } finally {
          setPatrolRunning(false);
        }
      },
      testid: "avatar-menu-run-patrol",
    },
    {
      key: "logout",
      label: "Logout",
      icon: LogOut,
      onClick: () => logout(),
      testid: "avatar-menu-logout",
    },
  ].filter(Boolean);

  const close = useCallback(() => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  }, []);

  useOnClickOutside(menuRef, () => open && setOpen(false));

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((i) => (i + 1) % items.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((i) => (i - 1 + items.length) % items.length);
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const item = items[focusIndex];
        if (item && !item.disabled) {
          item.onClick();
          if (item.key !== "run-patrol") close();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, focusIndex, items, close]);

  useEffect(() => {
    if (open) setFocusIndex(0);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={currentUserEmail || "Account menu"}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="topnav-avatar-trigger"
        className="inline-flex items-center gap-1 h-8 pl-1 pr-2 rounded-full
          bg-slate-800 border border-slate-700/50 text-slate-200
          hover:bg-slate-700 hover:text-white transition-colors duration-150"
      >
        <span
          className="w-6 h-6 rounded-full bg-teal/20 border border-teal/30 text-[10px] font-bold text-teal flex items-center justify-center"
          data-testid="topnav-avatar-initials"
        >
          {initials}
        </span>
        <ChevronDown size={12} className="opacity-70" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          data-testid="topnav-avatar-menu"
          className="absolute right-0 mt-1 w-52 bg-navy-card border border-slate-700/50 rounded-lg shadow-lg z-50 py-1"
        >
          {items.map((item, idx) => {
            const Icon = item.icon;
            const focused = idx === focusIndex;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                data-testid={item.testid}
                onMouseEnter={() => setFocusIndex(idx)}
                onClick={() => {
                  if (item.disabled) return;
                  item.onClick();
                  if (item.key !== "run-patrol") close();
                }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left
                  ${focused ? "bg-slate-700/60 text-white" : "text-slate-300"}
                  ${item.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-700/60 hover:text-white"}
                  transition-colors`}
              >
                <Icon
                  size={14}
                  className={`shrink-0 ${item.iconSpin ? "animate-spin" : ""}`}
                />
                <span className="flex-1">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
